import React from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {LinearGradient} from 'react-native-linear-gradient';
import {Image} from 'react-native';
import TextInput from '../textInput';
import {hdp, wdp} from '../../utils/responsive';
import {tr} from '../../translations';
import TextGeneric from '../textGeneric';
import {Theme} from '../../theme/lightTheme';
import {QuestionInputProps} from '../../interfaces/components/questionInput';
import {RootState} from '../../redux/store';
import {useSelector} from 'react-redux';
import Config from 'react-native-config';

const Container = styled.View`
  width: 100%;
  flex-direction: row;
`;
const BlurViewContainer = styled.View`
  border-radius: 10px;
  height: ${hdp(15)}px;
  width: 100%;
  overflow: hidden;
  border-width: 1px;
  border-color: rgba(255, 255, 255, 0.2);
  background-color: rgba(0, 0, 0, 0.1);
`;
const QuestionPlaceholderContainer = styled.View`
  position: absolute;
  top: 0;
  width: 100%;
  padding: ${hdp(1) + 5}px ${hdp(1) + 10}px;
  padding-right: 0;
`;
const QuestionPlaceholder = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme, bold}) =>
    bold ? theme.questionInput.text : theme.questionInput.placeholder};
  text-align: left;
`;
const ImageContainer = styled.View`
  width: ${hdp(7.5)}px;
  height: ${hdp(14)}px;
  overflow: hidden;
  z-index: 10;
  position: absolute;
  right: ${wdp(3)}px;
  top: ${hdp(0.5)}px;
`;
/**
 * QuestionInput component.
 *
 * @param {QuestionInputProps} props - Props including style customizations and additional TextInput properties.
 * @returns {React.ReactElement} The stylized QuestionInput component with conditional theming and localized placeholders.
 */
const QuestionInput: React.FC<QuestionInputProps> = ({style, ...props}) => {
  const theme = useTheme() as Theme;
  const {currentUser} = useSelector((state: RootState) => state.auth) || {
    currentUser: {},
  };
  const _renderQuestionPlaceholder = () => {
    if (currentUser?.email?.trim().toLowerCase() === Config.APPLE_EMAIL) {
      return (
        <QuestionPlaceholderContainer>
          <QuestionPlaceholder bold>Advanced Search ...</QuestionPlaceholder>
        </QuestionPlaceholderContainer>
      );
    }
    return (
      <QuestionPlaceholderContainer>
        {props.customPlaceholder ? (
          <QuestionPlaceholder>
            {tr(props.customPlaceholder)}
          </QuestionPlaceholder>
        ) : (
          <QuestionPlaceholder>
            <QuestionPlaceholder bold>
              {tr('questionInput.placeholderPart1')}
            </QuestionPlaceholder>
            {tr('questionInput.placeholderPart2')}
          </QuestionPlaceholder>
        )}
      </QuestionPlaceholderContainer>
    );
  };
  return (
    <Container style={style}>
      <BlurViewContainer>
        <LinearGradient
          start={{x: 0, y: 0}}
          end={{x: 1, y: 0}}
          colors={[
            theme.questionInput.startColor,
            theme.questionInput.endColor,
          ]}
          style={{
            height: '100%',
            width: '100%',
          }}>
          <TextInput
            {...props}
            placeholderComponent={_renderQuestionPlaceholder}
            selectionColor={theme.questionInput.text}
            style={{
              height: '100%',
              textAlignVertical: 'top',
              color: theme.questionInput.text,
              fontSize: theme.text.s8,
              lineHeight: theme.text.s8 * 1.4,
            }}
            inputContainerStyle={{
              padding: hdp(1),
              borderWidth: 0,
              borderRadius: 10,
              height: hdp(15),
              width: wdp(87) - hdp(7.5),
              backgroundColor: 'transparent',
            }}
            containerStyle={{
              marginBottom: hdp(2),
            }}
            multiline
            scrollEnabled
          />
        </LinearGradient>
      </BlurViewContainer>
    </Container>
  );
};

export default QuestionInput;
